import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  RefreshCw,
  Loader,
  FolderOpen,
  FileText,
  FileSpreadsheet,
  Network,
  Building2,
  Calculator,
  Trash2,
  MapPin,
  X,
  AlertCircle,
  Info,
  Download,
  ExternalLink,
  Pencil,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import useAuthUser from '../hooks/useAuthUser';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import { useRequireModuleAccess } from '../hooks/useRequireModuleAccess.ts';
import ModuleAccessDeniedDialog from './shared/ModuleAccessDeniedDialog.tsx';
import AssessmentReportsEditPanel from './assessment-reports-edit/AssessmentReportsEditPanel.jsx';
import MaterialityCalculationEditCard from './assessment-reports-edit/MaterialityCalculationEditCard.jsx';
import CompanyProfileEditCard from './assessment-reports-edit/CompanyProfileEditCard.jsx';
import EntityScopingConclusionsEditor from './assessment-reports-edit/EntityScopingConclusionsEditor.jsx';
import EntityScopingUploadEditCard from './assessment-reports-edit/EntityScopingUploadEditCard.jsx';
import {
  getSoxCycles,
  getSoxCycleColor,
  getSoxCycleName,
  resolveIncomeStatementMappingByItemId
} from '../utils/soxCycleReference';
import { getAvailableSoxCyclesFromApiData, resolveSoxCyclesWithDefaults } from '../utils/soxCycleDefaults';
import { useSoxDefaultsMappings } from '../hooks/useSoxDefaultsMappings';
import { Tooltip, TooltipProvider } from '../admin/components/shared/Tooltip.tsx';
import {
  normalizeIncomeStatementItemCategory,
  normalizeBalanceSheetItemCategory,
  balanceSheetSoxItemTypePrefix
} from '../utils/balanceSheetLineCategory';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
// access_token is now handled via cookies; no direct token usage here.

/** Pure transform for saved assessment line items → sectioned UI shape (stable for useCallback deps). */
/**
 * Fallback: backfill `notes` on raw risk-score items from the separately-persisted
 * `lineItemNotes` map saved by Materiality.js. Mutates a copy and returns it.
 * Notes keys (from Materiality.js): `bs_<docIdx>_<idx>` / `is_<docIdx>_<idx>` /
 * `is_revenue_<docIdx>_<idx>` / `is_expense_<docIdx>_<idx>` / `income_statement_<docIdx>_<idx>`.
 * We try each doc index so notes show up regardless of which document was active on save.
 */
function mergeLineItemNotesIntoRawData(rawData, lineItemNotes) {
  if (!rawData || !lineItemNotes || typeof lineItemNotes !== 'object') return rawData;
  const notes = lineItemNotes;
  const noteKeys = Object.keys(notes);
  if (noteKeys.length === 0) return rawData;

  const docIdxCandidates = Array.from(
    new Set(
      noteKeys
        .map((k) => {
          const m = k.match(/_(\d+)_\d+$/);
          return m ? parseInt(m[1], 10) : null;
        })
        .filter((n) => n != null)
    )
  );
  if (docIdxCandidates.length === 0) docIdxCandidates.push(0);

  const findBsNote = (idx) => {
    for (const d of docIdxCandidates) {
      const v = notes[`bs_${d}_${idx}`];
      if (v != null && v !== '') return v;
    }
    return null;
  };
  const findIsNote = (idx, category) => {
    const typed = category === 'revenue' ? 'is_revenue' : category === 'expense' ? 'is_expense' : null;
    for (const d of docIdxCandidates) {
      const keys = [
        `is_${d}_${idx}`,
        typed ? `${typed}_${d}_${idx}` : null,
        `income_statement_${d}_${idx}`
      ].filter(Boolean);
      for (const k of keys) {
        const v = notes[k];
        if (v != null && v !== '') return v;
      }
    }
    return null;
  };

  const next = { ...rawData };
  if (next.balance_sheet && Array.isArray(next.balance_sheet.items)) {
    next.balance_sheet = {
      ...next.balance_sheet,
      items: next.balance_sheet.items.map((item, idx) => {
        if (item.notes && String(item.notes).trim() !== '') return item;
        const fallback = findBsNote(idx);
        return fallback ? { ...item, notes: fallback } : item;
      })
    };
  }
  if (next.income_statement && Array.isArray(next.income_statement.items)) {
    next.income_statement = {
      ...next.income_statement,
      items: next.income_statement.items.map((item, idx) => {
        if (item.notes && String(item.notes).trim() !== '') return item;
        const fallback = findIsNote(idx, item.category);
        return fallback ? { ...item, notes: fallback } : item;
      })
    };
  }
  return next;
}

function transformSoxReportLineItemData(data) {
  if (!data) return null;

  const transformed = {
    balance_sheet: {},
    income_statement: {}
  };

  if (data.balance_sheet && data.balance_sheet.items) {
    const balanceSheetItems = data.balance_sheet.items;
    const notTotal = (item) => !(item.label || '').toLowerCase().includes('total');

    const assets = [];
    const liabilities = [];
    const equity = [];
    balanceSheetItems.forEach((item, flatIdx) => {
      if (!notTotal(item)) return;
      const cat = normalizeBalanceSheetItemCategory(item);
      const row = { ...item, _balanceSheetFlatIndex: flatIdx };
      if (cat === 'liability') liabilities.push(row);
      else if (cat === 'equity') equity.push(row);
      else assets.push(row);
    });

    transformed.balance_sheet = {
      assets: {
        items: assets,
        total: assets.reduce((sum, item) => sum + (item.value || 0), 0),
        units: assets.length > 0 ? assets[0].units : 'USD'
      },
      liabilities: {
        items: liabilities,
        total: liabilities.reduce((sum, item) => sum + (item.value || 0), 0),
        units: liabilities.length > 0 ? liabilities[0].units : 'USD'
      },
      equity: {
        items: equity,
        total: equity.reduce((sum, item) => sum + (item.value || 0), 0),
        units: equity.length > 0 ? equity[0].units : 'USD'
      }
    };
  }

  if (data.income_statement && data.income_statement.items) {
    const incomeStatementItems = data.income_statement.items;

    const revenue = [];
    const expenses = [];
    incomeStatementItems.forEach((item, flatIdx) => {
      const low = (item.label || '').toLowerCase();
      if (low.includes('total')) return;
      const enriched = { ...item, _isLineItemIndex: flatIdx };
      if (normalizeIncomeStatementItemCategory(item) === 'revenue') {
        revenue.push(enriched);
      } else {
        expenses.push(enriched);
      }
    });

    transformed.income_statement = {
      revenue: {
        items: revenue,
        total: revenue.reduce((sum, item) => sum + (item.value || 0), 0),
        units: revenue.length > 0 ? revenue[0].units : 'USD'
      },
      expenses: {
        items: expenses,
        total: expenses.reduce((sum, item) => sum + (item.value || 0), 0),
        units: expenses.length > 0 ? expenses[0].units : 'USD'
      }
    };
  }

  return transformed;
}

function rawAssessmentHasLineItemSoxCycles(rawData) {
  const bsItems = rawData?.balance_sheet?.items;
  const isItems = rawData?.income_statement?.items;
  const hasEmbedded = (items) =>
    Array.isArray(items) && items.some((item) => Array.isArray(item?.sox_cycles) && item.sox_cycles.length > 0);
  return hasEmbedded(bsItems) || hasEmbedded(isItems);
}

/**
 * Single implementation for BS/IS SOX resolution (table rows + unique-cycle rollup).
 */
function getReportLineItemSoxCycles(
  item,
  statement,
  indexInSection,
  customSoxMappings,
  soxDefaultsMappings,
  hasLineItemSoxCycles
) {
  if (statement === 'balance_sheet') {
    const slug = (item.label || '').replace(/\s+/g, '_').toLowerCase();
    const itemType = balanceSheetSoxItemTypePrefix(item);
    const flatIdx =
      item._balanceSheetFlatIndex !== undefined ? item._balanceSheetFlatIndex : indexInSection;
    const idSection = `${itemType}_${indexInSection}_${slug}`;
    const idFlat = `${itemType}_${flatIdx}_${slug}`;
    const customMapped =
      resolveIncomeStatementMappingByItemId(customSoxMappings, idSection) ??
      resolveIncomeStatementMappingByItemId(customSoxMappings, idFlat);
    if (!hasLineItemSoxCycles) {
      return customMapped || getSoxCycles(item.label);
    }
    return item.sox_cycles || customMapped || resolveSoxCyclesWithDefaults({
      mappings: soxDefaultsMappings,
      itemLabel: item.label,
    });
  }
  const itemType =
    normalizeIncomeStatementItemCategory(item) === 'revenue' ? 'is_revenue' : 'is_expense';
  const flatIdx =
    item._isLineItemIndex !== undefined ? item._isLineItemIndex : indexInSection;
  const itemId = `${itemType}_${flatIdx}_${(item.label || '').replace(/\s+/g, '_').toLowerCase()}`;
  const customMapped = resolveIncomeStatementMappingByItemId(customSoxMappings, itemId);
  if (!hasLineItemSoxCycles) {
    return customMapped || getSoxCycles(item.label);
  }
  return item.sox_cycles || customMapped || resolveSoxCyclesWithDefaults({
    mappings: soxDefaultsMappings,
    itemLabel: item.label,
  });
}

function SOXCycleMappingReport({ user }) {
  const { selectedAuditYearId } = useAuditYear();
  const authenticatedUser = useAuthUser(user);
  const [selectedTab, setSelectedTab] = useState('sox-cycles');
  const [expandedItems, setExpandedItems] = useState({});
  const [riskFilter, setRiskFilter] = useState('All Risk Levels');
  const [itemFilter, setItemFilter] = useState('All Items');

  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [isEditingAssessment, setIsEditingAssessment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lineItemData, setLineItemData] = useState(null);
  const [customSoxMappings, setCustomSoxMappings] = useState({});
  const [customSoxCycles, setCustomSoxCycles] = useState([]);
  const [hasLineItemSoxCycles, setHasLineItemSoxCycles] = useState(false);
  const { data: soxDefaultsData } = useSoxDefaultsMappings(true);
  const soxDefaultsMappings = soxDefaultsData?.mappings || null;
  const availableSoxCycles = useMemo(
    () => getAvailableSoxCyclesFromApiData(soxDefaultsData),
    [soxDefaultsData]
  );
  const getCycleDisplayName = useCallback((rawCode) => {
    const code = typeof rawCode === 'string' ? rawCode.trim() : String(rawCode ?? '').trim();
    if (!code) return '';
    const cycleInfo =
      availableSoxCycles.find((c) => c.code === code) ||
      customSoxCycles.find((c) => c.code === code);
    return cycleInfo?.name || getSoxCycleName(code) || code;
  }, [availableSoxCycles, customSoxCycles]);
  const [entityScopingData, setEntityScopingData] = useState(null);
  const [materialityData, setMaterialityData] = useState(null);
  const [documentsJson, setDocumentsJson] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState(null);

  // Component mounted animation
  const [isAnimated, setIsAnimated] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Entity selection state for detailed view
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [lineItemSearch, setLineItemSearch] = useState('');
  const [lineItemAmountSortDir, setLineItemAmountSortDir] = useState(null); // null | 'asc' | 'desc'

  // Notes modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);

  // Sync SOX cycles state
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncingCycles, setSyncingCycles] = useState(false);
  const {
    checkAccess: checkProcessesModuleAccess,
    dialogProps: processesModuleAccessDialogProps,
  } = useRequireModuleAccess(MODULE_KEYS.PROCESSES, {
    hint:
      'Syncing SOX cycles creates process records on the SOX Dashboard and requires the Processes module. Contact your organization administrator if you need access.',
  });

  const openSyncSoxCyclesModal = useCallback(() => {
    if (!checkProcessesModuleAccess()) return;
    setShowSyncModal(true);
  }, [checkProcessesModuleAccess]);
  /** True while GET /api/getUniqueSOXCycles is in flight */
  const [uniqueSoxCyclesLoading, setUniqueSoxCyclesLoading] = useState(true);
  /** True when backend returns a non-empty `cycles` array — sync button stays off */
  const [hasBackendUniqueSoxCycles, setHasBackendUniqueSoxCycles] = useState(false);

  useEffect(() => {
    // Trigger fade-in animation on mount
    const timer = setTimeout(() => setIsAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const loadAssessment = useCallback((assessment) => {
    setIsEditingAssessment(false);
    if (!assessment) {
      setSelectedAssessment(null);
      setLineItemData(null);
      setCustomSoxMappings({});
      setCustomSoxCycles([]);
      setHasLineItemSoxCycles(false);
      setEntityScopingData(null);
      setMaterialityData(null);
      setDocumentsJson(null);
      return;
    }

    setSelectedAssessment(assessment);

    if (assessment?.sox_cycle_json) {
      let rawData = assessment.sox_cycle_json.riskScores ?? {};

      if (Array.isArray(rawData) && rawData.length > 0) {
        rawData = rawData[0].lineItems;
      }

      // Fallback: merge notes from the separately-saved lineItemNotes map so the SOX cycles
      // tab shows notes even if they weren't embedded on the risk-score items at save time.
      const rawDataWithNotes = mergeLineItemNotesIntoRawData(
        rawData,
        assessment.sox_cycle_json.lineItemNotes || {}
      );
      setHasLineItemSoxCycles(rawAssessmentHasLineItemSoxCycles(rawDataWithNotes));

      const transformedData = transformSoxReportLineItemData(rawDataWithNotes);
      setLineItemData(transformedData);
      setCustomSoxMappings(assessment.sox_cycle_json.customSoxMappings || {});
      setCustomSoxCycles(assessment.sox_cycle_json.customSoxCycles || []);

    } else {
      setLineItemData(null);
      setCustomSoxMappings({});
      setCustomSoxCycles([]);
      setHasLineItemSoxCycles(false);
    }

    // Extract Documents data
    setEntityScopingData(assessment?.entityscoping_json || null);
    setMaterialityData(assessment?.materiality_json || null);
    setDocumentsJson(assessment?.documents_json || null);
  }, []);

  const fetchAssessments = useCallback(async () => {
    try {
      setLoading(true);
      const hasUserSession = authenticatedUser && (authenticatedUser.id != null || authenticatedUser.username);
      if (!hasUserSession) {
        setError('No user session found');
        setLoading(false);
        return;
      }
      const matParams = selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {};
      const response = await axiosInstance.get('/api/materiality-assessments', { params: matParams });

      if (response.data.success && response.data.assessments) {
        setAssessments(response.data.assessments);

        if (response.data.assessments.length > 0) {
          loadAssessment(response.data.assessments[0]);
        } else {
          loadAssessment(null);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching assessments:', err);
      setError('Failed to load assessments: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  }, [authenticatedUser, loadAssessment, selectedAuditYearId]);

  const fetchUniqueSoxCyclesStatus = useCallback(async () => {
    try {
      setUniqueSoxCyclesLoading(true);
      const hasUserSession = authenticatedUser && (authenticatedUser.id != null || authenticatedUser.username);
      if (!hasUserSession) {
        setHasBackendUniqueSoxCycles(false);
        return;
      }
      const response = await axiosInstance.get('/api/getUniqueSOXCycles');
      const cycles = response.data?.cycles;
      const hasData = Array.isArray(cycles) && cycles.length > 0;
      setHasBackendUniqueSoxCycles(hasData);
    } catch (err) {
      console.error('Error fetching unique SOX cycles:', err);
      setHasBackendUniqueSoxCycles(false);
    } finally {
      setUniqueSoxCyclesLoading(false);
    }
  }, [authenticatedUser]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  useEffect(() => {
    fetchUniqueSoxCyclesStatus();
  }, [fetchUniqueSoxCyclesStatus]);

  const handleVersionChange = (e) => {
    const assessmentId = parseInt(e.target.value, 10);
    const assessment = assessments.find(a => a.id === assessmentId);
    if (assessment) {
      loadAssessment(assessment);
      // Reset animation on version change
      setIsAnimated(false);
      setTimeout(() => setIsAnimated(true), 50);
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getRiskBadgeColor = (risk) => {
    if (!risk || risk === 0) return 'bg-gray-100 text-gray-800 border-gray-200';
    const score = parseFloat(risk);
    if (score <= 3.3) return 'bg-green-100 text-green-800 border-green-200';
    if (score <= 6.6) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getRiskLabel = (risk) => {
    if (!risk || risk === 0) return 'Not Assessed';
    const score = parseFloat(risk);
    if (score <= 3.3) return 'LOW';
    if (score <= 6.6) return 'MODERATE';
    return 'HIGH';
  };

  const getPdfRiskCellStyles = (riskLevel) => {
    const u = String(riskLevel || '').toUpperCase();
    if (u === 'LOW') {
      return { textColor: [22, 101, 52], fillColor: [220, 252, 231] };
    }
    if (u === 'MODERATE') {
      return { textColor: [133, 77, 14], fillColor: [254, 249, 195] };
    }
    if (u === 'HIGH') {
      return { textColor: [153, 27, 27], fillColor: [254, 226, 226] };
    }
    return { textColor: [55, 65, 81], fillColor: [243, 244, 246] };
  };

  const matchesRiskFilter = useCallback((item) => {
    if (riskFilter === 'All Risk Levels') return true;

    const rawRisk = item.risk_score || item.riskScore;
    const score = parseFloat(rawRisk);
    if (Number.isNaN(score) || score === 0) return false;

    if (riskFilter === 'High Risk') return score > 6.6;
    if (riskFilter === 'Moderate Risk') return score > 3.3 && score <= 6.6;
    if (riskFilter === 'Low Risk') return score <= 3.3;
    return true;
  }, [riskFilter]);


  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatLineItemValue = (value) => {
    if (value === null || value === undefined) return '$0';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(absValue);

    return isNegative ? `-$${formatted}` : `$${formatted}`;
  };

  // Helper function to format amounts (for entity scoping)
  const formatAmount = (amount) => {
    if (amount < 99999) {
      // Display full dollar amount with commas for amounts less than $99,999
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      // Display in millions for amounts >= $99,999
      return `$${(amount / 1000000).toFixed(2)}M`;
    }
  };

  useEffect(() => {
    setLineItemSearch('');
    setLineItemAmountSortDir(null);
  }, [selectedEntity?.data?.entity]);

  const isValidLineItemAccount = (item) => {
    const accountNum = item?.accountNumber;
    return (
      accountNum &&
      accountNum !== 'NA' &&
      accountNum !== 'N/A' &&
      accountNum.toString().toUpperCase() !== 'NA'
    );
  };

  const toggleLineItemAmountSort = () => {
    setLineItemAmountSortDir((prev) => {
      if (prev === null) return 'desc';
      if (prev === 'desc') return 'asc';
      return null;
    });
  };

  const displayedLineItems = useMemo(() => {
    const items = selectedEntity?.data?.lineItems || [];
    // Index after NA filter — stable across search/sort (matches prior row indexing)
    const withIdx = items
      .filter((item) => isValidLineItemAccount(item))
      .map((item, filteredIdx) => ({ item, origIdx: filteredIdx }));
    const q = lineItemSearch.trim().toLowerCase();
    let list = withIdx;
    if (q) {
      list = withIdx.filter(({ item }) => {
        const acct = String(item.accountNumber || '').toLowerCase();
        const desc = String(item.description || '').toLowerCase();
        return acct.includes(q) || desc.includes(q);
      });
    }
    if (!lineItemAmountSortDir) return list;
    const dir = lineItemAmountSortDir === 'asc' ? 1 : -1;
    return [...list].sort(
      (a, b) => ((Number(a.item.amount) || 0) - (Number(b.item.amount) || 0)) * dir
    );
  }, [selectedEntity, lineItemSearch, lineItemAmountSortDir]);

  const collectUniqueSoxCycles = useCallback(() => {
    const allSoxCycles = new Set();
    if (!lineItemData) return [];

    const addCycles = (cycles) => {
      if (!Array.isArray(cycles)) return;
      cycles.forEach((cycle) => {
        const c = typeof cycle === 'string' ? cycle.trim() : String(cycle ?? '').trim();
        if (c) allSoxCycles.add(c);
      });
    };

    if (lineItemData.balance_sheet) {
      Object.values(lineItemData.balance_sheet).forEach((section) => {
        if (!section?.items || !Array.isArray(section.items)) return;
        section.items.forEach((item, idx) => {
          addCycles(getReportLineItemSoxCycles(
            item,
            'balance_sheet',
            idx,
            customSoxMappings,
            soxDefaultsMappings,
            hasLineItemSoxCycles
          ));
        });
      });
    }
    if (lineItemData.income_statement) {
      Object.values(lineItemData.income_statement).forEach((section) => {
        if (!section?.items || !Array.isArray(section.items)) return;
        section.items.forEach((item, idx) => {
          addCycles(getReportLineItemSoxCycles(
            item,
            'income_statement',
            idx,
            customSoxMappings,
            soxDefaultsMappings,
            hasLineItemSoxCycles
          ));
        });
      });
    }
    return Array.from(allSoxCycles).sort();
  }, [lineItemData, customSoxMappings, soxDefaultsMappings, hasLineItemSoxCycles]);

  // ==========================================================================
  // Report export (PDF / Excel)
  // ==========================================================================

  /**
   * Sanitize a string for use as a download filename.
   */
  const buildExportFilename = useCallback((extension) => {
    const base = (selectedAssessment?.analysis_name || 'SOX_Mapping_Report')
      .replace(/[^a-z0-9]/gi, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    const date = new Date().toISOString().split('T')[0];
    return `${base}_${date}.${extension}`;
  }, [selectedAssessment]);

  /**
   * Flatten transformed balance sheet / income statement back into rows with the
   * same columns the user sees on screen: Line Item, Risk Score, Risk Level,
   * SOX Cycles, Amount, Notes.
   */
  const buildLineItemExportRows = useCallback(() => {
    const rows = { balance_sheet: [], income_statement: [] };
    if (!lineItemData) return rows;

    const riskLabel = (score) => {
      if (score == null || score === 0) return 'Not Assessed';
      const n = parseFloat(score);
      if (Number.isNaN(n)) return 'Not Assessed';
      if (n <= 3.3) return 'LOW';
      if (n <= 6.6) return 'MODERATE';
      return 'HIGH';
    };

    // Balance sheet — assets, liabilities, equity
    if (lineItemData.balance_sheet) {
      ['assets', 'liabilities', 'equity'].forEach((sectionName) => {
        const section = lineItemData.balance_sheet[sectionName];
        if (!section?.items) return;
        section.items.forEach((item, originalIndex) => {
          const soxCycles = getReportLineItemSoxCycles(
            item,
            'balance_sheet',
            originalIndex,
            customSoxMappings,
            soxDefaultsMappings,
            hasLineItemSoxCycles
          );
          const score = item.risk_score || item.riskScore;
          rows.balance_sheet.push({
            section: sectionName,
            lineItem: item.label || '',
            riskScore: score != null ? Number(score).toFixed(1) : '',
            riskLevel: riskLabel(score),
            soxCycles: Array.isArray(soxCycles) ? soxCycles.join(', ') : '',
            amount: item.value || 0,
            notes: item.notes || ''
          });
        });
      });
    }

    // Income statement — revenue, expenses
    if (lineItemData.income_statement) {
      ['revenue', 'expenses'].forEach((sectionName) => {
        const section = lineItemData.income_statement[sectionName];
        if (!section?.items) return;
        section.items.forEach((item, originalIndex) => {
          const soxCycles = getReportLineItemSoxCycles(
            item,
            'income_statement',
            originalIndex,
            customSoxMappings,
            soxDefaultsMappings,
            hasLineItemSoxCycles
          );
          const score = item.risk_score || item.riskScore;
          rows.income_statement.push({
            section: sectionName,
            lineItem: item.label || '',
            riskScore: score != null ? Number(score).toFixed(1) : '',
            riskLevel: riskLabel(score),
            soxCycles: Array.isArray(soxCycles) ? soxCycles.join(', ') : '',
            amount: item.value || 0,
            notes: item.notes || ''
          });
        });
      });
    }

    return rows;
  }, [lineItemData, customSoxMappings, soxDefaultsMappings, hasLineItemSoxCycles]);

  /**
   * Build Source Document rows for export including the filename, type, period,
   * and usage (materiality vs risk) derived from the saved documents_json.
   */
  const buildSourceDocumentExportRows = useCallback(() => {
    const docs = materialityData?.documentInfo?.documents;
    if (!Array.isArray(docs) || docs.length === 0) return [];
    return docs.map((doc, idx) => ({
      sourceFile: doc.sourceFile || `Document ${idx + 1}`,
      companyName: doc.companyName || 'N/A',
      documentType: doc.documentType || 'N/A',
      period: doc.period || 'N/A',
      periodMonths: doc.periodMonths || '',
      annualizationFactor: doc.annualizationFactor || 1
    }));
  }, [materialityData]);

  /**
   * Export the current assessment as a multi-sheet Excel workbook covering
   * the Materiality, SOX Cycles, Source Documents, and Entity Scoping tabs.
   */
  const handleExportToExcel = useCallback(async () => {
    if (!selectedAssessment) return;
    try {
      setExporting(true);
      const wb = XLSX.utils.book_new();
      const lineRows = buildLineItemExportRows();
      const docRows = buildSourceDocumentExportRows();
      const toExcelNumber = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? Math.round(n) : 0;
      };
      const applyExcelAmountFormat = (sheet, colIndexes, fromRow, toRow) => {
        for (let r = fromRow; r <= toRow; r += 1) {
          colIndexes.forEach((c) => {
            const addr = XLSX.utils.encode_cell({ r, c });
            const cell = sheet[addr];
            if (!cell || cell.v == null || cell.v === '') return;
            const n = Number(cell.v);
            if (!Number.isFinite(n)) return;
            cell.t = 'n';
            cell.v = Math.round(n);
            cell.z = '#,##0';
            delete cell.w;
          });
        }
      };

      // --- Overview sheet ---
      const cp = materialityData?.companyProfile;
      const cm = materialityData?.confirmedMateriality;
      const overviewRows = [
        ['SOX Cycle Mapping Report'],
        ['Analysis Name', selectedAssessment.analysis_name || ''],
        ['Generated', new Date().toLocaleString()],
        [],
        ['Company Profile'],
        ['Company Name', cp?.companyName || 'N/A'],
        ['Industry', cp?.industry || 'N/A'],
        ['Growth Stage', cp?.growthStage || 'N/A'],
        ['Total Revenue', toExcelNumber(cp?.totalRevenue || 0)],
        ['Total Assets', toExcelNumber(cp?.totalAssets || 0)],
        ['Net Income', toExcelNumber(cp?.netIncome || 0)],
        [],
        ['Materiality Calculation'],
        ['Selected Category', materialityData?.selectedCategory || 'N/A'],
        ['Sensitivity Level', materialityData?.sensitivityLevel || 'N/A'],
        ['Sensitivity %', materialityData?.sensitivityPercentage || ''],
        ['Overall Materiality', toExcelNumber(cm?.overallMateriality || 0)],
        ['Planning Materiality', toExcelNumber(cm?.planningMateriality || 0)],
        ['De Minimis', toExcelNumber(cm?.deminimis || 0)]
      ];
      if (materialityData?.overrideAmount || materialityData?.overrideReasoning) {
        overviewRows.push([]);
        overviewRows.push(['Materiality Adjustments']);
        if (materialityData.overrideAmount) {
          overviewRows.push(['Override Amount', toExcelNumber(parseFloat(materialityData.overrideAmount) || 0)]);
        }
        if (materialityData.overrideReasoning) {
          overviewRows.push(['Reasoning', materialityData.overrideReasoning]);
        }
      }
      const overviewSheet = XLSX.utils.aoa_to_sheet(overviewRows);
      overviewSheet['!cols'] = [{ wch: 28 }, { wch: 60 }];
      if (overviewSheet['!ref']) {
        const overviewRange = XLSX.utils.decode_range(overviewSheet['!ref']);
        applyExcelAmountFormat(overviewSheet, [1], overviewRange.s.r, overviewRange.e.r);
      }
      XLSX.utils.book_append_sheet(wb, overviewSheet, 'Overview');

      // --- Balance Sheet sheet ---
      if (lineRows.balance_sheet.length > 0) {
        const header = ['Section', 'Line Item', 'Risk Score', 'Risk Level', 'SOX Cycles', 'Amount', 'Notes'];
        const data = [header, ...lineRows.balance_sheet.map((r) => [
          r.section, r.lineItem, r.riskScore, r.riskLevel, r.soxCycles, toExcelNumber(r.amount), r.notes
        ])];
        const sheet = XLSX.utils.aoa_to_sheet(data);
        sheet['!cols'] = [
          { wch: 14 }, { wch: 40 }, { wch: 12 }, { wch: 14 }, { wch: 24 }, { wch: 18 }, { wch: 60 }
        ];
        applyExcelAmountFormat(sheet, [5], 1, lineRows.balance_sheet.length);
        XLSX.utils.book_append_sheet(wb, sheet, 'Balance Sheet');
      }

      // --- Income Statement sheet ---
      if (lineRows.income_statement.length > 0) {
        const header = ['Section', 'Line Item', 'Risk Score', 'Risk Level', 'SOX Cycles', 'Amount', 'Notes'];
        const data = [header, ...lineRows.income_statement.map((r) => [
          r.section, r.lineItem, r.riskScore, r.riskLevel, r.soxCycles, toExcelNumber(r.amount), r.notes
        ])];
        const sheet = XLSX.utils.aoa_to_sheet(data);
        sheet['!cols'] = [
          { wch: 14 }, { wch: 40 }, { wch: 12 }, { wch: 14 }, { wch: 24 }, { wch: 18 }, { wch: 60 }
        ];
        applyExcelAmountFormat(sheet, [5], 1, lineRows.income_statement.length);
        XLSX.utils.book_append_sheet(wb, sheet, 'Income Statement');
      }

      // --- Source Documents sheet ---
      if (docRows.length > 0) {
        const header = ['Source File', 'Company', 'Document Type', 'Period', 'Period (Months)', 'Annualization Factor'];
        const data = [header, ...docRows.map((d) => [
          d.sourceFile, d.companyName, d.documentType, d.period, d.periodMonths, d.annualizationFactor
        ])];
        const sheet = XLSX.utils.aoa_to_sheet(data);
        sheet['!cols'] = [
          { wch: 40 }, { wch: 25 }, { wch: 16 }, { wch: 20 }, { wch: 16 }, { wch: 20 }
        ];
        XLSX.utils.book_append_sheet(wb, sheet, 'Source Documents');
      }

      // --- Entity Scoping sheet ---
      const entityRows = entityScopingData?.entityScopingData;
      if (Array.isArray(entityRows) && entityRows.length > 0) {
        const header = ['Entity', 'Revenue', 'Costs', '% Revenue', '% Costs', 'Quant. Conclusion', 'Qualitative Comment', 'Final Conclusion'];
        const data = [header, ...entityRows.map((row) => [
          row.entity,
          toExcelNumber(row.revenue || 0),
          toExcelNumber(row.costs || 0),
          row.revPct != null ? `${row.revPct}%` : '',
          row.costPct != null ? `${row.costPct}%` : '',
          entityScopingData?.entityConclusions?.[row.entity] || row.conclusion || 'Not Assessed',
          entityScopingData?.qualitativeComments?.[row.entity] || '',
          entityScopingData?.finalConclusions?.[row.entity] ||
          entityScopingData?.entityConclusions?.[row.entity] || row.conclusion || 'Not Assessed'
        ])];
        const sheet = XLSX.utils.aoa_to_sheet(data);
        sheet['!cols'] = [
          { wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 20 }, { wch: 40 }, { wch: 20 }
        ];
        applyExcelAmountFormat(sheet, [1, 2], 1, entityRows.length);
        XLSX.utils.book_append_sheet(wb, sheet, 'Entity Scoping');
      }

      XLSX.writeFile(wb, buildExportFilename('xlsx'));
      setShowExportModal(false);
      toast.success('Excel report downloaded');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      toast.error('Failed to generate Excel report');
    } finally {
      setExporting(false);
    }
  }, [
    selectedAssessment, materialityData, entityScopingData,
    buildLineItemExportRows, buildSourceDocumentExportRows, buildExportFilename
  ]);

  /**
   * Export the current assessment as a PDF. Mirrors the on-screen report:
   * Materiality (company profile + calculation), SOX Cycles (BS + IS line
   * item tables), Source Documents, and Entity Scoping.
   */
  const handleExportToPDFReport = useCallback(async () => {
    if (!selectedAssessment) return;
    try {
      setExporting(true);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const fmtCurrency = (value) => {
        if (value == null || value === '') return '$0';
        return new Intl.NumberFormat('en-US', {
          style: 'currency', currency: 'USD',
          minimumFractionDigits: 0, maximumFractionDigits: 0
        }).format(Number(value) || 0);
      };

      // Cover page
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text(selectedAssessment.analysis_name || 'SOX Mapping Report', pageWidth / 2, 50, { align: 'center' });
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'normal');
      pdf.text('SOX Cycle Mapping Report', pageWidth / 2, 62, { align: 'center' });
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 76, { align: 'center' });
      const cp = materialityData?.companyProfile;
      if (cp?.companyName) {
        pdf.text(`Company: ${cp.companyName}`, pageWidth / 2, 86, { align: 'center' });
      }

      // ----- SECTION 1: MATERIALITY -----
      pdf.addPage();
      let yPos = 20;
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(55, 65, 81);
      pdf.text('SECTION 1: MATERIALITY', margin, yPos);
      yPos += 8;
      pdf.setDrawColor(209, 213, 219);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      if (cp) {
        const cpRows = [
          ['Company Name', cp.companyName || 'N/A'],
          ['Industry', cp.industry || 'N/A'],
          ['Growth Stage', cp.growthStage || 'N/A'],
          ['Total Revenue', fmtCurrency(cp.totalRevenue)],
          ['Total Assets', fmtCurrency(cp.totalAssets)],
          ['Net Income', fmtCurrency(cp.netIncome)]
        ];
        autoTable(pdf, {
          startY: yPos,
          head: [['Company Profile', '']],
          body: cpRows,
          theme: 'striped',
          headStyles: { fillColor: [99, 102, 241], textColor: [255, 255, 255], fontSize: 10 },
          bodyStyles: { fontSize: 9, textColor: [55, 65, 81] },
          columnStyles: { 0: { cellWidth: 60, fontStyle: 'bold' }, 1: { cellWidth: 100 } },
          margin: { left: margin, right: margin }
        });
        yPos = pdf.lastAutoTable.finalY + 8;
      }

      const cm = materialityData?.confirmedMateriality;
      if (cm) {
        const cmRows = [
          ['Selected Category', materialityData.selectedCategory || 'N/A'],
          ['Sensitivity Level', materialityData.sensitivityLevel || 'N/A'],
          ['Sensitivity %', materialityData.sensitivityPercentage || 'N/A'],
          ['Overall Materiality', fmtCurrency(cm.overallMateriality)],
          ['Planning Materiality', fmtCurrency(cm.planningMateriality)],
          ['De Minimis', fmtCurrency(cm.deminimis)]
        ];
        autoTable(pdf, {
          startY: yPos,
          head: [['Materiality Calculation', '']],
          body: cmRows,
          theme: 'striped',
          headStyles: { fillColor: [147, 51, 234], textColor: [255, 255, 255], fontSize: 10 },
          bodyStyles: { fontSize: 9, textColor: [55, 65, 81] },
          columnStyles: { 0: { cellWidth: 60, fontStyle: 'bold' }, 1: { cellWidth: 100 } },
          margin: { left: margin, right: margin }
        });
        yPos = pdf.lastAutoTable.finalY + 8;
      }

      const docs = materialityData?.documentInfo?.documents || [];
      if (docs.length > 0) {
        if (yPos > pageHeight - 60) { pdf.addPage(); yPos = 20; }
        const docRows = docs.map((doc, idx) => [
          doc.sourceFile || `Document ${idx + 1}`,
          doc.companyName || 'N/A',
          doc.documentType || 'N/A',
          doc.period || 'N/A',
          String(doc.periodMonths || ''),
          doc.annualizationFactor > 1 ? `×${doc.annualizationFactor}` : '—'
        ]);
        autoTable(pdf, {
          startY: yPos,
          head: [['Source File', 'Company', 'Type', 'Period', 'Months', 'Annualized']],
          body: docRows,
          theme: 'grid',
          headStyles: { fillColor: [79, 70, 229], textColor: [255, 255, 255], fontSize: 9 },
          bodyStyles: { fontSize: 8, textColor: [55, 65, 81] },
          margin: { left: margin, right: margin }
        });
        yPos = pdf.lastAutoTable.finalY + 8;
      }

      // ----- SECTION 2: SOX CYCLES -----
      pdf.addPage();
      yPos = 20;
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(55, 65, 81);
      pdf.text('SECTION 2: SOX CYCLES', margin, yPos);
      yPos += 8;
      pdf.setDrawColor(209, 213, 219);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      const uniqueCycles = collectUniqueSoxCycles();
      if (uniqueCycles.length > 0) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text(`Unique SOX Cycles Mapped: ${uniqueCycles.length}`, margin, yPos);
        yPos += 6;
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(55, 65, 81);
        const cycleText = uniqueCycles.map((c) => `${c} - ${getCycleDisplayName(c)}`).join('  |  ');
        const cycleLines = pdf.splitTextToSize(cycleText, pageWidth - 2 * margin);
        pdf.text(cycleLines, margin, yPos);
        yPos += cycleLines.length * 4 + 4;
      }

      const lineRows = buildLineItemExportRows();
      if (lineRows.balance_sheet.length > 0) {
        if (yPos > pageHeight - 40) { pdf.addPage(); yPos = 20; }
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(17, 24, 39);
        pdf.text('Balance Sheet Line Items', margin, yPos);
        yPos += 6;

        const bsBody = [];
        const bsRowMeta = [];
        let lastBsSection = null;
        lineRows.balance_sheet.forEach((r) => {
          if (r.section && r.section !== lastBsSection) {
            lastBsSection = r.section;
            bsBody.push([r.section, '', '', '', '']);
            bsRowMeta.push({ type: 'section' });
          }
          bsBody.push([
            r.lineItem,
            r.riskScore ? `${r.riskLevel} (${r.riskScore})` : r.riskLevel,
            r.soxCycles,
            fmtCurrency(r.amount),
            r.notes
          ]);
          bsRowMeta.push({ type: 'item', riskLevel: r.riskLevel });
        });

        autoTable(pdf, {
          startY: yPos,
          head: [['Line Item', 'Risk', 'SOX Cycles', 'Amount', 'Notes']],
          body: bsBody,
          theme: 'striped',
          headStyles: { fillColor: [99, 102, 241], textColor: [255, 255, 255], fontSize: 9 },
          bodyStyles: { fontSize: 8, textColor: [55, 65, 81] },
          columnStyles: {
            0: { cellWidth: 50 },
            1: { cellWidth: 28 },
            2: { cellWidth: 35 },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 42 }
          },
          margin: { left: margin, right: margin },
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            const meta = bsRowMeta[data.row.index];
            if (!meta) return;
            if (meta.type === 'section') {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [243, 244, 246];
              data.cell.styles.textColor = [17, 24, 39];
              return;
            }
            if (data.column.index !== 1) return;
            const styles = getPdfRiskCellStyles(meta.riskLevel);
            data.cell.styles.textColor = styles.textColor;
            data.cell.styles.fillColor = styles.fillColor;
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.halign = 'center';
          },
        });
        yPos = pdf.lastAutoTable.finalY + 8;
      }

      if (lineRows.income_statement.length > 0) {
        if (yPos > pageHeight - 40) { pdf.addPage(); yPos = 20; }
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(17, 24, 39);
        pdf.text('Income Statement Line Items', margin, yPos);
        yPos += 6;

        const isBody = [];
        const isRowMeta = [];
        let lastIsSection = null;
        lineRows.income_statement.forEach((r) => {
          if (r.section && r.section !== lastIsSection) {
            lastIsSection = r.section;
            isBody.push([r.section, '', '', '', '']);
            isRowMeta.push({ type: 'section' });
          }
          isBody.push([
            r.lineItem,
            r.riskScore ? `${r.riskLevel} (${r.riskScore})` : r.riskLevel,
            r.soxCycles,
            fmtCurrency(r.amount),
            r.notes
          ]);
          isRowMeta.push({ type: 'item', riskLevel: r.riskLevel });
        });

        autoTable(pdf, {
          startY: yPos,
          head: [['Line Item', 'Risk', 'SOX Cycles', 'Amount', 'Notes']],
          body: isBody,
          theme: 'striped',
          headStyles: { fillColor: [34, 197, 94], textColor: [255, 255, 255], fontSize: 9 },
          bodyStyles: { fontSize: 8, textColor: [55, 65, 81] },
          columnStyles: {
            0: { cellWidth: 50 },
            1: { cellWidth: 28 },
            2: { cellWidth: 35 },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 42 }
          },
          margin: { left: margin, right: margin },
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            const meta = isRowMeta[data.row.index];
            if (!meta) return;
            if (meta.type === 'section') {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [243, 244, 246];
              data.cell.styles.textColor = [17, 24, 39];
              return;
            }
            if (data.column.index !== 1) return;
            const styles = getPdfRiskCellStyles(meta.riskLevel);
            data.cell.styles.textColor = styles.textColor;
            data.cell.styles.fillColor = styles.fillColor;
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.halign = 'center';
          },
        });
        yPos = pdf.lastAutoTable.finalY + 8;
      }

      // ----- SECTION 3: ENTITY SCOPING -----
      const entityRows = entityScopingData?.entityScopingData;
      if (Array.isArray(entityRows) && entityRows.length > 0) {
        pdf.addPage();
        yPos = 20;
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(55, 65, 81);
        pdf.text('SECTION 3: ENTITY SCOPING', margin, yPos);
        yPos += 8;
        pdf.setDrawColor(209, 213, 219);
        pdf.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;

        const em = entityScopingData.editableMateriality;
        if (em) {
          const om = em.overallMateriality || em;
          const pm = em.planningMateriality || 0;
          const dm = em.deminimis || 0;
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(55, 65, 81);
          pdf.text(
            `Overall Materiality: ${fmtCurrency(om)}    Planning: ${fmtCurrency(pm)}    De Minimis: ${fmtCurrency(dm)}`,
            margin, yPos
          );
          yPos += 8;
        }

        autoTable(pdf, {
          startY: yPos,
          head: [['Entity', 'Revenue', 'Costs', '% Rev', '% Cost', 'Quant.', 'Qualitative', 'Final']],
          body: entityRows.map((row) => [
            row.entity,
            fmtCurrency(row.revenue),
            fmtCurrency(row.costs),
            row.revPct != null ? `${row.revPct}%` : '',
            row.costPct != null ? `${row.costPct}%` : '',
            entityScopingData?.entityConclusions?.[row.entity] || row.conclusion || 'Not Assessed',
            (entityScopingData?.qualitativeComments?.[row.entity] || '').slice(0, 60),
            entityScopingData?.finalConclusions?.[row.entity] ||
            entityScopingData?.entityConclusions?.[row.entity] || row.conclusion || 'Not Assessed'
          ]),
          theme: 'grid',
          headStyles: { fillColor: [249, 250, 251], textColor: [55, 65, 81], fontSize: 7 },
          bodyStyles: { fontSize: 7, textColor: [55, 65, 81] },
          columnStyles: {
            0: { cellWidth: 22, fontStyle: 'bold' },
            1: { cellWidth: 22, halign: 'right' },
            2: { cellWidth: 22, halign: 'right' },
            3: { cellWidth: 13, halign: 'right' },
            4: { cellWidth: 13, halign: 'right' },
            5: { cellWidth: 22 },
            6: { cellWidth: 40 },
            7: { cellWidth: 22 }
          },
          margin: { left: margin, right: margin }
        });
      }

      pdf.save(buildExportFilename('pdf'));
      setShowExportModal(false);
      toast.success('PDF report downloaded');
    } catch (err) {
      console.error('Error exporting to PDF:', err);
      toast.error('Failed to generate PDF report');
    } finally {
      setExporting(false);
    }
  }, [
    selectedAssessment, materialityData, entityScopingData,
    collectUniqueSoxCycles, buildLineItemExportRows, buildExportFilename, getCycleDisplayName
  ]);
  /**
   * Resolve a file_id for a given document in the saved materiality assessment.
   * Prefers the file_id embedded on materialityData.documentInfo.documents[idx], and
   * falls back to documents_json.uploadedFiles matched by filename.
   */
  const resolveFileIdForDocument = useCallback((doc, idx) => {
    if (!doc) return null;
    if (doc.file_id) return doc.file_id;
    const uploaded = documentsJson?.uploadedFiles;
    if (Array.isArray(uploaded)) {
      // Match by filename first (most reliable across save/load); fallback to positional index.
      const byName = uploaded.find(
        (u) => u && (u.name === doc.sourceFile || u.name === doc.filename)
      );
      if (byName?.file_id) return byName.file_id;
      if (uploaded[idx]?.file_id) return uploaded[idx].file_id;
    }
    return null;
  }, [documentsJson]);

  /**
   * Fetch a short-lived signed GET URL for the given file_id and open it in a new tab.
   */
  const handleOpenSourceDocument = useCallback(async (fileId, fallbackName) => {
    if (!fileId) {
      toast.error('This document was saved without a file reference and cannot be opened.');
      return;
    }
    try {
      setDownloadingFileId(fileId);
      const response = await axiosInstance.get(`/api/files/${fileId}/download-url`);
      const url = response.data?.url;
      if (!url) {
        toast.error('Could not generate a download URL for this document.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error fetching document download URL:', err);
      toast.error(
        err.response?.data?.message || err.response?.data?.error ||
        `Failed to open ${fallbackName || 'document'}`
      );
    } finally {
      setDownloadingFileId(null);
    }
  }, []);

  /**
   * For a list of materiality documents, compute which ones were used for the
   * materiality calculation vs the risk assessment, based on documents_json indices.
   * Returns a function (idx, sourceFile) => { usedForMateriality, usedForRisk }.
   */
  const getDocumentUsage = useCallback(() => {
    const selForMateriality = documentsJson?.selectedForMateriality;
    const selForRisk = documentsJson?.selectedForRisk;
    const uploaded = documentsJson?.uploadedFiles || [];

    // Map each materialityData doc index → uploadedFiles index (by filename), so the
    // materiality/risk flags line up even if the two arrays are ordered differently.
    return (idx, sourceFile) => {
      let uploadedIdx = idx;
      if (sourceFile) {
        const byName = uploaded.findIndex((u) => u && u.name === sourceFile);
        if (byName >= 0) uploadedIdx = byName;
      }
      return {
        usedForMateriality: Array.isArray(selForMateriality)
          ? selForMateriality.includes(uploadedIdx)
          : true,
        usedForRisk: Array.isArray(selForRisk)
          ? selForRisk.includes(uploadedIdx)
          : false
      };
    };
  }, [documentsJson]);

  const handleDeleteAssessment = async () => {
    if (!selectedAssessment) return;

    try {
      setDeleting(true);
      const userHash = localStorage.getItem('userHash');

      await axiosInstance.delete(`/api/materiality-assessments/${selectedAssessment.id}`, {
        headers: {
          'Authorization': userHash
        }
      });

      // Close modal
      setShowDeleteModal(false);

      // Remove from assessments list
      const updatedAssessments = assessments.filter(a => a.id !== selectedAssessment.id);
      setAssessments(updatedAssessments);

      // Select the next most recent assessment or clear if none left
      if (updatedAssessments.length > 0) {
        loadAssessment(updatedAssessments[0]);
      } else {
        setSelectedAssessment(null);
        setLineItemData(null);
        setEntityScopingData(null);
        setMaterialityData(null);
      }

      setDeleting(false);
    } catch (err) {
      console.error('Error deleting assessment:', err);
      setError(
        resolve403ErrorMessage(
          err,
          'Failed to delete assessment: ' + (err.response?.data?.error || err.message)
        )
      );
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleSyncSoxCycles = async () => {
    if (!selectedAssessment) return;
    if (!checkProcessesModuleAccess()) return;

    try {
      setSyncingCycles(true);
      const response = await axiosInstance.post('/api/sync-sox-cycles-from-assessment', {
        assessmentId: selectedAssessment.id
      });

      if (response.data?.success) {
        toast.success(`SOX cycles synced successfully from "${selectedAssessment.analysis_name}"`);
        setShowSyncModal(false);
        fetchUniqueSoxCyclesStatus();
      } else {
        toast.error(
          response.data?.message ||
          response.data?.error ||
          'Sync did not complete successfully. Please try again.'
        );
      }
    } catch (error) {
      console.error('Error syncing SOX cycles:', error);
      toast.error(
        resolve403ErrorMessage(
          error,
          error.response?.data?.error || error.response?.data?.message || 'Failed to sync SOX cycles'
        )
      );
    } finally {
      setSyncingCycles(false);
    }
  };

  const renderBalanceSheetSection = (section, sectionName) => {
    if (!section || !section.items) return null;

    const filteredEntries = section.items
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => matchesRiskFilter(item));

    if (filteredEntries.length === 0) return null;

    const filteredTotal = filteredEntries.reduce((sum, { item }) => sum + (item.value || 0), 0);

    return (
      <div key={sectionName} className="mb-6">
        <div
          className="grid grid-cols-12 gap-4 py-3 border-b hover:bg-gray-50 cursor-pointer bg-gray-50"
          onClick={() => toggleExpand(sectionName)}
        >
          <div className="col-span-5 flex items-center gap-2">
            {expandedItems[sectionName] ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
            <span className="font-semibold text-gray-900">{sectionName}</span>
          </div>
          <div className="col-span-2 text-sm font-medium text-gray-600">
            {filteredEntries.length} items
          </div>
          <div className="col-span-3"></div>
          <div className="col-span-2 text-sm font-semibold text-gray-900">
            {formatLineItemValue(filteredTotal)}
          </div>
        </div>

        {expandedItems[sectionName] && (
          <div className="ml-8">
            {filteredEntries.map(({ item, originalIndex }) => {
              // Risk score is embedded in the item object
              const riskScore = item.risk_score || item.riskScore;
              const soxCycles = getReportLineItemSoxCycles(
                item,
                'balance_sheet',
                originalIndex,
                customSoxMappings,
                soxDefaultsMappings,
                hasLineItemSoxCycles
              );

              return (
                <div
                  key={originalIndex}
                  className={`relative z-0 hover:z-30 grid grid-cols-12 gap-4 py-3 border-b hover:bg-gray-50 transform transition-all duration-300 ${expandedItems[sectionName]
                    ? 'translate-x-0 opacity-100'
                    : '-translate-x-4 opacity-0'
                    }`}
                  style={{ transitionDelay: `${originalIndex * 50}ms` }}
                >
                  <div className="col-span-5 flex items-center gap-2">
                    <ChevronRight className="h-3 w-3 text-gray-300" />
                    <span className="text-gray-700">{item.label}</span>
                    {item.notes && item.notes.trim() !== '' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLineItem({
                            label: item.label,
                            notes: item.notes,
                            value: item.value,
                            units: item.units,
                            riskScore: riskScore,
                            soxCycles: soxCycles
                          });
                          setShowNotesModal(true);
                        }}
                        className="ml-1 p-1 hover:bg-blue-100 rounded-full transition-colors"
                        title="View notes"
                      >
                        <Info className="h-4 w-4 text-blue-600" />
                      </button>
                    )}
                  </div>
                  <div className="col-span-2 flex items-center">
                    {riskScore ? (
                      <span className={`px-2 py-1 rounded-full border text-xs font-medium ${getRiskBadgeColor(riskScore)}`}>
                        {getRiskLabel(riskScore)} ({riskScore.toFixed(1)})
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Not assessed</span>
                    )}
                  </div>
                  <div className="col-span-3 flex gap-2 flex-wrap">
                    {soxCycles.length > 0 ? (
                      soxCycles.map((cycle, idx) => {
                        const code = typeof cycle === 'string' ? cycle.trim() : String(cycle ?? '');
                        const cycleTitle = getCycleDisplayName(code);
                        return (
                          <div key={idx} className="relative group">
                            <span
                              className={`px-2 py-1 rounded border text-xs font-medium ${getSoxCycleColor(code)} cursor-help`}
                            >
                              {code}
                            </span>
                            {cycleTitle && (
                              <div className="pointer-events-none absolute left-0 top-full mt-1 hidden group-hover:block z-50 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                                {cycleTitle}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <span className="text-xs text-gray-400">No mapping</span>
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-gray-900 font-medium">
                    {formatLineItemValue(item.value)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderIncomeStatementSection = (section, sectionName) => {
    if (!section || !section.items) return null;

    const filteredEntries = section.items
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => matchesRiskFilter(item));

    if (filteredEntries.length === 0) return null;

    const filteredTotal = filteredEntries.reduce((sum, { item }) => sum + (item.value || 0), 0);

    return (
      <div key={sectionName} className="mb-6">
        <div
          className="grid grid-cols-12 gap-4 py-3 border-b hover:bg-gray-50 cursor-pointer bg-gray-50"
          onClick={() => toggleExpand(sectionName)}
        >
          <div className="col-span-5 flex items-center gap-2">
            {expandedItems[sectionName] ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
            <span className="font-semibold text-gray-900">{sectionName}</span>
          </div>
          <div className="col-span-2 text-sm font-medium text-gray-600">
            {filteredEntries.length} items
          </div>
          <div className="col-span-3"></div>
          <div className="col-span-2 text-sm font-semibold text-gray-900">
            {formatLineItemValue(filteredTotal)}
          </div>
        </div>

        {expandedItems[sectionName] && (
          <div className="ml-8">
            {filteredEntries.map(({ item, originalIndex }) => {
              // Risk score is embedded in the item object
              const riskScore = item.risk_score || item.riskScore;
              const flatIdx =
                item._isLineItemIndex !== undefined ? item._isLineItemIndex : originalIndex;
              const soxCycles = getReportLineItemSoxCycles(
                item,
                'income_statement',
                originalIndex,
                customSoxMappings,
                soxDefaultsMappings,
                hasLineItemSoxCycles
              );

              return (
                <div
                  key={`${sectionName}-${flatIdx}-${originalIndex}`}
                  className={`relative z-0 hover:z-30 grid grid-cols-12 gap-4 py-3 border-b hover:bg-gray-50 transform transition-all duration-300 ${expandedItems[sectionName]
                    ? 'translate-x-0 opacity-100'
                    : '-translate-x-4 opacity-0'
                    }`}
                  style={{ transitionDelay: `${originalIndex * 50}ms` }}
                >
                  <div className="col-span-5 flex items-center gap-2">
                    <ChevronRight className="h-3 w-3 text-gray-300" />
                    <span className="text-gray-700">{item.label}</span>
                    {item.notes && item.notes.trim() !== '' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLineItem({
                            label: item.label,
                            notes: item.notes,
                            value: item.value,
                            units: item.units,
                            riskScore: riskScore,
                            soxCycles: soxCycles
                          });
                          setShowNotesModal(true);
                        }}
                        className="ml-1 p-1 hover:bg-blue-100 rounded-full transition-colors"
                        title="View notes"
                      >
                        <Info className="h-4 w-4 text-blue-600" />
                      </button>
                    )}
                  </div>
                  <div className="col-span-2 flex items-center">
                    {riskScore ? (
                      <span className={`px-2 py-1 rounded-full border text-xs font-medium ${getRiskBadgeColor(riskScore)}`}>
                        {getRiskLabel(riskScore)} ({riskScore.toFixed(1)})
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Not assessed</span>
                    )}
                  </div>
                  <div className="col-span-3 flex gap-2 flex-wrap">
                    {soxCycles.length > 0 ? (
                      soxCycles.map((cycle, idx) => {
                        const code = typeof cycle === 'string' ? cycle.trim() : String(cycle ?? '');
                        const cycleTitle = getCycleDisplayName(code);
                        return (
                          <div key={idx} className="relative group">
                            <span
                              className={`px-2 py-1 rounded border text-xs font-medium ${getSoxCycleColor(code)} cursor-help`}
                            >
                              {code}
                            </span>
                            {cycleTitle && (
                              <div className="pointer-events-none absolute left-0 top-full mt-1 hidden group-hover:block z-50 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                                {cycleTitle}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <span className="text-xs text-gray-400">No mapping</span>
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-gray-900 font-medium">
                    {formatLineItemValue(item.value)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading saved assessments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Assessments</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchAssessments}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <FolderOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Saved Assessments</h2>
          <p className="text-gray-600">Complete a materiality assessment and save it to view SOX cycle mappings here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 transition-opacity duration-500 ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">SOX Cycle Mapping Report</h1>
          <p className="text-gray-600 mt-2">Review financial statement line items mapped to SOX business cycles</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Version Selector */}
        <div className="bg-white rounded-lg shadow p-6 mb-6 transform transition-all duration-500"
          style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(-10px)' }}>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4 flex-1">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Select Assessment Version
              </label>
              <select
                value={selectedAssessment?.id || ''}
                onChange={handleVersionChange}
                className="flex-1 max-w-md px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {assessments.map(assessment => (
                  <option key={assessment.id} value={assessment.id}>
                    {assessment.analysis_name} - {new Date(assessment.created_at).toLocaleDateString()} at {new Date(assessment.created_at).toLocaleTimeString()}
                  </option>
                ))}
              </select>
            </div>
            {selectedAssessment && (
              <>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Analysis
                </button>
                <button
                  onClick={() => setShowExportModal(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
              </>
            )}
          </div>
        </div>

        {isEditingAssessment && selectedAssessment ? (
          <div className="mb-6">
            <AssessmentReportsEditPanel
              assessment={selectedAssessment}
              onCancel={() => setIsEditingAssessment(false)}
              onSaved={(updated) => {
                setAssessments((prev) =>
                  prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a))
                );
                loadAssessment(updated);
                setIsEditingAssessment(false);
              }}
            />
          </div>
        ) : null}

        {/* Tabs + view content — hidden while Assessment Reports edit is open */}
        {!isEditingAssessment && (
        <>
        <div className="bg-white rounded-lg shadow mb-6 transform transition-all duration-500"
          style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(-10px)' }}>
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => {
                  setSelectedTab('materiality');
                  setIsAnimated(false);
                  setTimeout(() => setIsAnimated(true), 50);
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 flex items-center gap-2 ${selectedTab === 'materiality'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Calculator className="h-4 w-4" />
                Materiality
              </button>
              <button
                onClick={() => {
                  setSelectedTab('sox-cycles');
                  setIsAnimated(false);
                  setTimeout(() => setIsAnimated(true), 50);
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 flex items-center gap-2 ${selectedTab === 'sox-cycles'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Network className="h-4 w-4" />
                SOX Cycles
              </button>
              <button
                onClick={() => {
                  setSelectedTab('entity-scoping');
                  setIsAnimated(false);
                  setTimeout(() => setIsAnimated(true), 50);
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 flex items-center gap-2 ${selectedTab === 'entity-scoping'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Building2 className="h-4 w-4" />
                Entity Scoping
              </button>
            </nav>
          </div>
        </div>

        {/* SOX Cycles Tab Content */}
        {selectedTab === 'sox-cycles' && lineItemData && (
          <div className="space-y-6">
            {/* Unique SOX Cycles Summary */}
            {(() => {
              const uniqueCycles = collectUniqueSoxCycles();
              const uniqueCycleCount = uniqueCycles.length;

              // Build cycle full names
              const cycleFullNames = {
                'ELC': 'Entity Level Controls',
                'FSC': 'Financial Statement Close',
                'ER': 'External Reporting',
                'OTC': 'Order to Cash',
                'TRS': 'Treasury',
                'DBT': 'Debt',
                'PTP': 'Procure to Pay',
                'HTR': 'Hire to Retire',
                'INV': 'Inventory',
                'TAX': 'Tax',
                'FIX': 'Fixed Assets',
                'LEA': 'Leases',
                'EQY': 'Equity',
                'PTR': 'Procure to Retire',
                'RCM': 'Record to Maintain',
                'STK': 'Stock Compensation',
                'FLC': 'Fixed Asset / Liability Cycle',
                'EXT': 'External Investments'
              };

              // Add custom cycle names
              customSoxCycles.forEach(cycle => {
                cycleFullNames[cycle.code] = cycle.name;
              });

              // Add custom cycle names from availableSoxCycles
              availableSoxCycles.forEach(cycle => {
                cycleFullNames[cycle.code] = cycle.name;
              });

              return (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg transform transition-all duration-700"
                  style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(20px)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-900">
                          <span className="font-semibold">Unique SOX Cycles Mapped:</span> {uniqueCycleCount} cycle{uniqueCycleCount !== 1 ? 's' : ''}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {uniqueCycles.map((cycle) => {
                            const code = typeof cycle === 'string' ? cycle.trim() : String(cycle ?? '');
                            const cycleTitle = getCycleDisplayName(code) || cycleFullNames[code] || code;
                            return (
                              <div key={code} className="relative group">
                                <span className="inline-flex px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium cursor-help border border-blue-200">
                                  {code}
                                </span>
                                <div className="pointer-events-none absolute left-0 top-full mt-1 hidden group-hover:block z-50 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                                  {cycleTitle}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <TooltipProvider>
                      {uniqueSoxCyclesLoading || hasBackendUniqueSoxCycles ? (
                        <Tooltip
                          side="top"
                          content={
                            hasBackendUniqueSoxCycles
                              ? "SOX cycles already exist."
                              : 'Sync SOX cycles to the SOX Dashboard'
                          }
                        >
                          <span className="inline-flex">
                            <button
                              type="button"
                              onClick={openSyncSoxCyclesModal}
                              disabled={uniqueSoxCyclesLoading || hasBackendUniqueSoxCycles}
                              className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${uniqueSoxCyclesLoading || hasBackendUniqueSoxCycles
                                ? 'bg-indigo-400 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700'
                                }`}
                            >
                              <RefreshCw className={`h-4 w-4 ${uniqueSoxCyclesLoading ? 'animate-spin' : ''}`} />
                              {uniqueSoxCyclesLoading ? 'Checking…' : 'Sync SOX Cycles'}
                            </button>
                          </span>
                        </Tooltip>
                      ) : (
                        <button
                          type="button"
                          onClick={openSyncSoxCyclesModal}
                          className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex-shrink-0 bg-indigo-600 hover:bg-indigo-700"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Sync SOX Cycles
                        </button>
                      )}
                    </TooltipProvider>
                  </div>
                </div>
              );
            })()}

            <div className="bg-white rounded-lg shadow p-6 transform transition-all duration-700"
              style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(20px)' }}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Financial Statement Line Items - SOX Mapping</h2>
                <div className="flex items-center gap-3">
                  <select
                    value={riskFilter}
                    onChange={(e) => setRiskFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option>All Risk Levels</option>
                    <option>High Risk</option>
                    <option>Moderate Risk</option>
                    <option>Low Risk</option>
                  </select>
                  <select
                    value={itemFilter}
                    onChange={(e) => setItemFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option>All Items</option>
                    <option>Balance Sheet</option>
                    <option>Income Statement</option>
                  </select>
                  {selectedAssessment && (
                    <button
                      type="button"
                      onClick={() => setIsEditingAssessment(true)}
                      className="px-4 py-2 bg-white text-indigo-700 border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-2"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </button>
                  )}
                </div>
              </div>

              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 pb-3 border-b text-sm font-medium text-gray-600 mb-2">
                <div className="col-span-5">Line Item</div>
                <div className="col-span-2">Risk Score</div>
                <div className="col-span-3">SOX Cycles</div>
                <div className="col-span-2">Amount</div>
              </div>

              {/* Balance Sheet */}
              {(itemFilter === 'All Items' || itemFilter === 'Balance Sheet') && lineItemData.balance_sheet && (
                <div className="mb-8">
                  <div
                    className="grid grid-cols-12 gap-4 py-3 bg-indigo-50 border-b cursor-pointer"
                    onClick={() => toggleExpand('balance-sheet')}
                  >
                    <div className="col-span-12 flex items-center gap-2">
                      {expandedItems['balance-sheet'] ? (
                        <ChevronDown className="h-5 w-5 text-indigo-600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-indigo-600" />
                      )}
                      <span className="font-bold text-indigo-900 text-lg">Balance Sheet</span>
                    </div>
                  </div>

                  {expandedItems['balance-sheet'] && (
                    <div className="mt-4">
                      {Object.entries(lineItemData.balance_sheet).map(([sectionName, section]) => {
                        if (sectionName === 'total' || !section.items) return null;
                        return renderBalanceSheetSection(section, sectionName);
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Income Statement */}
              {(itemFilter === 'All Items' || itemFilter === 'Income Statement') && lineItemData.income_statement && (
                <div>
                  <div
                    className="grid grid-cols-12 gap-4 py-3 bg-green-50 border-b cursor-pointer"
                    onClick={() => toggleExpand('income-statement')}
                  >
                    <div className="col-span-12 flex items-center gap-2">
                      {expandedItems['income-statement'] ? (
                        <ChevronDown className="h-5 w-5 text-green-600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-green-600" />
                      )}
                      <span className="font-bold text-green-900 text-lg">Income Statement</span>
                    </div>
                  </div>

                  {expandedItems['income-statement'] && (
                    <div className="mt-4">
                      {Object.entries(lineItemData.income_statement).map(([sectionName, section]) => {
                        if (sectionName === 'total' || !section.items) return null;
                        return renderIncomeStatementSection(section, sectionName);
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Materiality Assessment Tab Content */}
        {selectedTab === 'materiality' && (
          <div className="space-y-6 transform transition-all duration-700"
            style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(20px)', opacity: isAnimated ? 1 : 0 }}>

            {materialityData ? (
              <>
                {materialityData.companyProfile && selectedAssessment && (
                  <CompanyProfileEditCard
                    assessment={selectedAssessment}
                    materialityData={materialityData}
                    onSaved={(updated) => {
                      setAssessments((prev) =>
                        prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a))
                      );
                      loadAssessment(updated);
                    }}
                  />
                )}

                {/* Financial Metrics */}
                {materialityData.companyProfile && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Financial Metrics</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-600 mb-2">Total Revenue</label>
                        <p className="text-2xl font-bold text-indigo-900">{formatCurrency(materialityData.companyProfile.totalRevenue)}</p>
                        <p className="text-xs text-gray-500 mt-1">Source: {materialityData.companyProfile.revenueSource}</p>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-600 mb-2">Total Assets</label>
                        <p className="text-2xl font-bold text-indigo-900">{formatCurrency(materialityData.companyProfile.totalAssets)}</p>
                        <p className="text-xs text-gray-500 mt-1">Source: {materialityData.companyProfile.assetsSource}</p>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-600 mb-2">Net Income</label>
                        <p className={`text-2xl font-bold ${materialityData.companyProfile.netIncome < 0 ? 'text-red-900' : 'text-green-900'}`}>
                          {formatCurrency(materialityData.companyProfile.netIncome)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Source: {materialityData.companyProfile.netIncomeSource}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Materiality Calculation */}
                {materialityData.confirmedMateriality && selectedAssessment && (
                  <MaterialityCalculationEditCard
                    assessment={selectedAssessment}
                    materialityData={materialityData}
                    formatCurrency={formatCurrency}
                    onSaved={(updated) => {
                      setAssessments((prev) =>
                        prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a))
                      );
                      loadAssessment(updated);
                    }}
                  />
                )}

                {/* Document Sources */}
                {materialityData.documentInfo && materialityData.documentInfo.documents && (() => {
                  const usageFor = getDocumentUsage();
                  return (
                    <div className="bg-white rounded-lg shadow p-6">
                      <h2 className="text-xl font-semibold text-gray-900 mb-6">Source Documents</h2>
                      <div className="grid gap-4">
                        {materialityData.documentInfo.documents.map((doc, idx) => {
                          const fileId = resolveFileIdForDocument(doc, idx);
                          const { usedForMateriality, usedForRisk } = usageFor(idx, doc.sourceFile);
                          const isDownloading = downloadingFileId === fileId;
                          return (
                            <div
                              key={idx}
                              className={`border border-gray-200 rounded-lg p-4 transition-all duration-200 ${fileId
                                ? 'hover:shadow-md hover:border-indigo-300 cursor-pointer'
                                : 'opacity-90'
                                }`}
                              onClick={() => fileId && handleOpenSourceDocument(fileId, doc.sourceFile)}
                              role={fileId ? 'button' : undefined}
                              tabIndex={fileId ? 0 : undefined}
                              onKeyDown={(e) => {
                                if (fileId && (e.key === 'Enter' || e.key === ' ')) {
                                  e.preventDefault();
                                  handleOpenSourceDocument(fileId, doc.sourceFile);
                                }
                              }}
                              title={fileId ? 'Click to open source document' : 'Source file reference unavailable'}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <FileText className="h-6 w-6 text-indigo-600 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-medium text-gray-900 truncate">{doc.sourceFile}</h4>
                                      {fileId && (
                                        isDownloading ? (
                                          <Loader className="h-4 w-4 text-indigo-500 animate-spin" />
                                        ) : (
                                          <ExternalLink className="h-4 w-4 text-indigo-500" />
                                        )
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600">{doc.companyName} • {doc.documentType}</p>
                                    <p className="text-xs text-gray-500 mt-1">Period: {doc.period} ({doc.periodMonths} months)</p>
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                      {usedForMateriality && (
                                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-800 border border-indigo-200">
                                          Used for Materiality
                                        </span>
                                      )}
                                      {usedForRisk && (
                                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800 border border-purple-200">
                                          Used for Risk Assessment
                                        </span>
                                      )}
                                      {!fileId && (
                                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600 border border-gray-200">
                                          File not linked
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {doc.annualizationFactor > 1 && (
                                  <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 flex-shrink-0">
                                    Annualized (×{doc.annualizationFactor})
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-4">
                        Click any document to open the original file in a new tab. Download links expire after a short period.
                      </p>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center py-12">
                  <Calculator className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No materiality data available for this assessment</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Entity Scoping Tab Content */}
        {selectedTab === 'entity-scoping' && (
          <div className="space-y-6 transform transition-all duration-700"
            style={{ transform: isAnimated ? 'translateY(0)' : 'translateY(20px)', opacity: isAnimated ? 1 : 0 }}>

            {entityScopingData && entityScopingData.entityScopingData && entityScopingData.entityScopingData.length > 0 ? (
              <>
                {/* Section 1: Entity Scoping Analysis */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Section 1: Entity Scoping Analysis</h2>
                  <p className="text-gray-600 mb-6">Review materiality assessment by location. Click on any row to view detailed trial balance accounts in Section 2.</p>

                  {/* Materiality Thresholds */}
                  {entityScopingData.editableMateriality && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Materiality Thresholds</h4>
                      <div className="grid grid-cols-3 gap-6">
                        <div>
                          <label className="text-sm text-gray-600 block mb-2">Overall Materiality</label>
                          <p className="text-xl font-semibold text-gray-900">{formatCurrency(entityScopingData.editableMateriality.overallMateriality || entityScopingData.editableMateriality)}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 block mb-2">Planning Materiality</label>
                          <p className="text-xl font-semibold text-gray-900">{formatCurrency(entityScopingData.editableMateriality.planningMateriality || 0)}</p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 block mb-2">Sum De Minimis</label>
                          <p className="text-xl font-semibold text-gray-900">{formatCurrency(entityScopingData.editableMateriality.deminimis || 0)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Location Scoping Table — only conclusions editable */}
                  <EntityScopingConclusionsEditor
                    assessment={selectedAssessment}
                    entityScopingData={entityScopingData}
                    selectedEntityKey={selectedEntity?.data?.entity}
                    onSelectEntity={(row) => setSelectedEntity({ type: 'location', data: row })}
                    onSaved={(updated) => {
                      setAssessments((prev) =>
                        prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a))
                      );
                      loadAssessment(updated);
                    }}
                  />
                </div>

                {/* Section 2: Trial Balance Account Details */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Section 2: Trial Balance Account Details</h3>
                  <p className="text-gray-600 mb-6">Detailed trial balance accounts for the selected entity.</p>

                  {!selectedEntity ? (
                    <div className="border border-gray-200 rounded-lg p-12 text-center bg-gray-50">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 text-lg">
                        Select a row from the Location Scoping table above to view detailed trial balance breakdown.
                      </p>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg bg-white">
                      {/* Selected Entity Header */}
                      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <MapPin className="w-5 h-5 text-blue-600" />
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">{selectedEntity.data.entity}</h4>
                              <p className="text-sm text-gray-600">
                                Revenue: {formatAmount(selectedEntity.data.revenue)} |
                                Costs: {formatAmount(selectedEntity.data.costs)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEntity(null);
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Line Items Table */}
                      {selectedEntity.data.lineItems && selectedEntity.data.lineItems.some(isValidLineItemAccount) ? (
                        <div>
                          <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap items-end gap-3">
                            <div>
                              <label htmlFor="ar-line-item-search" className="mb-1 block text-xs font-medium text-gray-600">
                                Search Account # / Description
                              </label>
                              <input
                                id="ar-line-item-search"
                                type="search"
                                value={lineItemSearch}
                                onChange={(e) => setLineItemSearch(e.target.value)}
                                placeholder="Search account # or description…"
                                className="w-64 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                              />
                            </div>
                          </div>
                          <div className="overflow-auto max-h-[600px]">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Account #
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Description
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <button
                                      type="button"
                                      onClick={toggleLineItemAmountSort}
                                      className="inline-flex items-center gap-1 hover:text-indigo-700"
                                    >
                                      Amount
                                      {lineItemAmountSortDir === null && <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />}
                                      {lineItemAmountSortDir === 'asc' && <ArrowUp className="h-3.5 w-3.5 text-indigo-600" />}
                                      {lineItemAmountSortDir === 'desc' && <ArrowDown className="h-3.5 w-3.5 text-indigo-600" />}
                                    </button>
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {displayedLineItems.length === 0 ? (
                                  <tr>
                                    <td colSpan={3} className="px-6 py-8 text-center text-sm text-gray-500">
                                      No line items match the current search.
                                    </td>
                                  </tr>
                                ) : null}
                                {displayedLineItems.map(({ item, origIdx }) => (
                                  <tr key={origIdx} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {item.accountNumber}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-700">
                                      {item.description || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                      {formatAmount(item.amount || 0)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="p-12 text-center">
                          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-600">No line items found for this entity.</p>
                        </div>
                      )}

                      {/* Summary Footer */}
                      {selectedEntity.data.lineItems && selectedEntity.data.lineItems.some(isValidLineItemAccount) ? (
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                          <p className="text-sm text-gray-600">
                            Total Line Items: <span className="font-semibold text-gray-900">{displayedLineItems.length}</span>
                          </p>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <EntityScopingUploadEditCard
                assessment={selectedAssessment}
                user={authenticatedUser}
                onSaved={(updated) => {
                  setAssessments((prev) =>
                    prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a))
                  );
                  loadAssessment(updated);
                }}
              />
            )}
          </div>
        )}
        </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Analysis</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <span className="font-semibold">{selectedAssessment?.analysis_name}</span>?
              All data associated with this analysis will be permanently removed.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAssessment}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal (PDF / Excel) */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Download className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Export Report</h3>
                  <p className="text-sm text-gray-500">Choose a format to download</p>
                </div>
              </div>
              <button
                onClick={() => !exporting && setShowExportModal(false)}
                disabled={exporting}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                aria-label="Close export modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-gray-700 mb-6">
              Export <span className="font-semibold">{selectedAssessment?.analysis_name}</span> as
              a PDF document or an Excel workbook. The export includes the Materiality calculation,
              SOX cycle line items (with notes), source documents, and entity scoping data.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleExportToPDFReport}
                disabled={exporting}
                className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-indigo-200 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 hover:border-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? (
                  <Loader className="h-8 w-8 animate-spin" />
                ) : (
                  <FileText className="h-8 w-8" />
                )}
                <span className="font-semibold">PDF</span>
                <span className="text-xs text-indigo-600">Formatted report</span>
              </button>
              <button
                onClick={handleExportToExcel}
                disabled={exporting}
                className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-green-200 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 hover:border-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exporting ? (
                  <Loader className="h-8 w-8 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-8 w-8" />
                )}
                <span className="font-semibold">Excel</span>
                <span className="text-xs text-green-600">Multi-sheet workbook</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync SOX Cycles Confirmation Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <RefreshCw className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Sync SOX Cycles</h3>
                <p className="text-sm text-gray-500">Sync cycles to the SOX Dashboard</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              This will sync all SOX cycles from <span className="font-semibold">{selectedAssessment?.analysis_name}</span> to the SOX Cycles Dashboard.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSyncModal(false)}
                disabled={syncingCycles}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSyncSoxCycles}
                disabled={syncingCycles}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncingCycles ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Sync Cycles
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Line Item Notes Modal */}
      {showNotesModal && selectedLineItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Line Item Details</h3>
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setSelectedLineItem(null);
                }}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Line Item Name */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Line Item</label>
                <p className="text-lg font-semibold text-gray-900">{selectedLineItem.label}</p>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Amount</label>
                <p className="text-lg font-semibold text-gray-900">
                  {formatLineItemValue(selectedLineItem.value)}
                </p>
              </div>

              {/* Risk Score */}
              {selectedLineItem.riskScore && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Risk Assessment</label>
                  <span className={`inline-block px-3 py-1.5 rounded-full border text-sm font-medium ${getRiskBadgeColor(selectedLineItem.riskScore)}`}>
                    {getRiskLabel(selectedLineItem.riskScore)} ({selectedLineItem.riskScore.toFixed(1)})
                  </span>
                </div>
              )}

              {/* SOX Cycles */}
              {selectedLineItem.soxCycles && selectedLineItem.soxCycles.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">SOX Cycles</label>
                  <div className="flex flex-wrap gap-2">
                    {selectedLineItem.soxCycles.map((cycle, idx) => {
                      const code = typeof cycle === 'string' ? cycle.trim() : String(cycle ?? '');
                      const cycleTitle = getCycleDisplayName(code);
                      return (
                        <div key={idx} className="relative group">
                          <span
                            className={`px-3 py-1.5 rounded border text-sm font-medium ${getSoxCycleColor(code)} cursor-help`}
                          >
                            {code}
                          </span>
                          {cycleTitle && (
                            <div className="pointer-events-none absolute left-0 top-full mt-1 hidden group-hover:block z-50 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                              {cycleTitle}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Notes</label>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedLineItem.notes}</p>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setSelectedLineItem(null);
                }}
                className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <ModuleAccessDeniedDialog {...processesModuleAccessDialogProps} />
    </div>
  );
}

export default SOXCycleMappingReport;
